import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ExternalLink, Copy, Trash2, Pencil, Plus, Loader2 } from "lucide-react";

const EMPTY_FORM = { title: "", username: "", password: "", url: "", notes: "" };

export default function PersonalVault() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id,...} or null=new, or false=closed
  const [form, setForm] = useState(EMPTY_FORM);
  const [showKeys, setShowKeys] = useState({});
  const [revealed, setRevealed] = useState({});
  const [revealLoading, setRevealLoading] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/personal/vault").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({}); setForm(EMPTY_FORM); setShowKeys({}); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, username: "", password: "", url: item.url || "", notes: item.notes || "" }); setShowKeys({}); };
  const toggleShow = (key) => setShowKeys((s) => ({ ...s, [key]: !s[key] }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Başlık zorunludur");
    try {
      if (editing?.id) {
        await api.put(`/personal/vault/${editing.id}`, form);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/personal/vault", form);
        toast.success("Kayıt eklendi");
      }
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/personal/vault/${delTarget.id}`);
      toast.success("Kayıt silindi");
      setRevealed((r) => { const n = { ...r }; delete n[delTarget.id]; return n; });
      setDelTarget(null); load();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleReveal = async (item) => {
    if (revealed[item.id]) {
      setRevealed((r) => { const n = { ...r }; delete n[item.id]; return n; });
      return;
    }
    setRevealLoading(item.id);
    try {
      const { data } = await api.get(`/personal/vault/${item.id}/reveal`);
      setRevealed((r) => ({ ...r, [item.id]: data.values }));
    } catch (e) { toast.error(e.response?.data?.detail || "Görüntülenemedi"); }
    finally { setRevealLoading(null); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Kopyalandı"); };
  const go = (url) => window.open(url.startsWith("http") ? url : `https://${url}`, "_blank", "noopener,noreferrer");

  if (loading) return <div className="text-muted-foreground text-sm">Yükleniyor...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground flex-1">
          Bilgiler şifrelenerek saklanır. Görüntülediğinizde işlem kaydına (audit log) düşer. Sadece yönetici (siz) erişebilir.
        </p>
        <Button data-testid="pv-new-btn" onClick={openNew}><Plus size={16} className="mr-1.5" /> Yeni Kayıt</Button>
      </div>

      {items.length === 0 && <Card className="p-10 text-center text-muted-foreground">Henüz kayıtlı şifre yok.</Card>}

      {items.map((item) => {
        const rev = revealed[item.id];
        return (
          <Card key={item.id} data-testid={`pv-row-${item.id}`} className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <KeyRound size={16} className="text-emerald-600" />
                {item.title}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {item.url && (
                  <Button size="sm" variant="outline" onClick={() => go(item.url)}>
                    <ExternalLink size={14} className="mr-1.5" />Git
                  </Button>
                )}
                {(item.has_username || item.has_password) && (
                  <Button
                    size="sm" variant="outline" data-testid={`pv-reveal-${item.id}`}
                    onClick={() => toggleReveal(item)} disabled={revealLoading === item.id}
                  >
                    {revealLoading === item.id ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : rev ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
                    {rev ? "Gizle" : "Göster"}
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid={`pv-edit-${item.id}`} onClick={() => openEdit(item)}>
                  <Pencil size={14} className="mr-1.5" />Düzenle
                </Button>
                <Button size="sm" variant="ghost" data-testid={`pv-del-${item.id}`} onClick={() => setDelTarget(item)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>

            {item.notes && <p className="text-xs text-muted-foreground">{item.notes}</p>}

            {rev && (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2">
                {[["username", "Kullanıcı Adı"], ["password", "Şifre"]].map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
                    <span className="font-mono flex-1 truncate">{rev[k] || "—"}</span>
                    {rev[k] && (
                      <Button size="sm" variant="ghost" onClick={() => copy(rev[k])}>
                        <Copy size={13} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Kaydı Düzenle" : "Yeni Şifre Kaydı"}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Bilgiler şifrelenerek saklanır. Kullanıcı adı/şifreyi boş bırakırsanız mevcut değer korunur.</p>
          <div className="space-y-3 py-1">
            <div className="space-y-1"><Label className="text-xs">Başlık *</Label>
              <Input data-testid="pv-input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn. Ziraat Bankası İnternet Şubesi" />
            </div>
            <div className="space-y-1"><Label className="text-xs">Kullanıcı Adı</Label>
              <div className="relative">
                <Input
                  data-testid="pv-input-username"
                  type={showKeys.username ? "text" : "password"}
                  value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="off" className="pr-9"
                />
                <button type="button" tabIndex={-1} onClick={() => toggleShow("username")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showKeys.username ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Şifre</Label>
              <div className="relative">
                <Input
                  data-testid="pv-input-password"
                  type={showKeys.password ? "text" : "password"}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password" className="pr-9"
                />
                <button type="button" tabIndex={-1} onClick={() => toggleShow("password")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showKeys.password ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">URL</Label>
              <Input data-testid="pv-input-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-1"><Label className="text-xs">Notlar</Label>
              <Input data-testid="pv-input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>İptal</Button>
            <Button data-testid="pv-save" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı sil</AlertDialogTitle>
            <AlertDialogDescription><strong>{delTarget?.title}</strong> kalıcı olarak silinecek. Devam edilsin mi?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="pv-confirm-delete" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
